/**
 * Base URL + low-level fetch plumbing shared by every endpoint function in
 * this client. Endpoint modules never call `fetch` directly - they go
 * through `apiFetch` so the base URL, error handling, and request headers
 * stay consistent in one place.
 */
const DEFAULT_BASE_URL = "http://localhost:8000";
let baseUrl = DEFAULT_BASE_URL;
let authToken = null;
/** Point the client at a different backend. Call once at app startup. */
export function configureApiClient(config) {
    baseUrl = config.baseUrl.replace(/\/+$/, "");
}
/** The base URL currently in effect (defaults to http://localhost:8000). */
export function getApiBaseUrl() {
    return baseUrl;
}
/**
 * The bearer token attached to every request from here on (via `apiFetch`
 * and the hand-rolled `fetch` calls in `ingestion.ts`), or `null` to stop
 * attaching one. An app's `AuthContext` calls this once on login/logout/
 * startup-hydration - individual endpoint functions never touch it.
 */
export function setAuthToken(token) {
    authToken = token;
}
let unauthorizedHandler = null;
/**
 * Called once when a request that carried a token is answered 401 (the sign-in expired or was revoked). The app's `AuthContext` registers
 * it to sign the person out and show the login page with a message, so no form is left saving to a dead session.
 */
export function setUnauthorizedHandler(handler) {
    unauthorizedHandler = handler;
}
/** The `Authorization` header to send with the current token, or `{}` when logged out. */
export function authHeader() {
    return authToken ? { Authorization: `Bearer ${authToken}` } : {};
}
/** Raised for any non-2xx response, carrying the status and parsed body when available. */
export class ApiError extends Error {
    constructor(message, status, url, body) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.url = url;
        this.body = body;
    }
}
/**
 * What the server said went wrong, in its own words, or null when it said nothing readable. FastAPI sends `detail` as a
 * string (an error the code raised on purpose) or, for a rejected request body, a list of `{loc, msg}`; the list is turned
 * into "field: reason" lines, with pydantic's "Value error, " prefix removed. Use it instead of the generic
 * "failed with status 422" message.
 */
export function apiErrorDetail(error) {
    if (!(error instanceof ApiError))
        return null;
    const body = error.body;
    if (!body || typeof body !== "object" || !("detail" in body))
        return null;
    const detail = body.detail;
    if (typeof detail === "string")
        return detail.trim() || null;
    if (!Array.isArray(detail))
        return null;
    const lines = detail
        .map((item) => {
        if (!item || typeof item !== "object")
            return null;
        const { loc, msg } = item;
        if (typeof msg !== "string")
            return null;
        const reason = msg.replace(/^Value error,\s*/i, "");
        const field = Array.isArray(loc) ? loc.filter((part) => part !== "body" && typeof part === "string").join(" › ") : "";
        return field ? `${field.replace(/_/g, " ")}: ${reason}` : reason;
    })
        .filter((line) => Boolean(line));
    return lines.length ? lines.join("\n") : null;
}
/**
 * An array value is sent as a repeated query param (`?status=a&status=b`),
 * matching FastAPI's own convention for a `list[...]`-typed query
 * parameter - never comma-joined into one value, which a backend expecting
 * a repeated param would reject outright (a single "a,b" doesn't parse as
 * either enum member) rather than silently misinterpret.
 */
function buildQueryString(query) {
    if (!query)
        return "";
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null || value === "")
            continue;
        if (Array.isArray(value)) {
            for (const item of value)
                params.append(key, String(item));
            continue;
        }
        params.set(key, String(value));
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
}
const DEFAULT_REQUEST_TIMEOUT_MS = 15000;
/** Shared request helper: builds the URL, sends JSON, and throws ApiError on failure. */
export async function apiFetch(path, options = {}) {
    const { method = "GET", body, query, signal, timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS } = options;
    const url = `${baseUrl}${path}${buildQueryString(query)}`;
    const timeoutSignal = AbortSignal.timeout(timeoutMs);
    const requestSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;
    let response;
    try {
        response = await fetch(url, {
            method,
            signal: requestSignal,
            headers: {
                Accept: "application/json",
                ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
                ...authHeader(),
            },
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
    }
    catch (error) {
        if (error instanceof DOMException && error.name === "TimeoutError") {
            throw new ApiError(`Request to ${url} timed out after ${timeoutMs / 1000}s — is the API running at ${baseUrl}?`, 0, url, null);
        }
        throw error;
    }
    const text = await response.text();
    let parsed;
    if (text) {
        try {
            parsed = JSON.parse(text);
        }
        catch {
            // Backend returned a non-JSON body (e.g. a raw "Internal Server Error"
            // from an unhandled 500) - fall back to the raw text so callers still
            // get an ApiError instead of a parse crash.
            parsed = text;
        }
    }
    if (response.status === 401 && authToken && unauthorizedHandler) {
        // Only a request that carried a token: a wrong password at the login form (no token yet) is answered 401 too and is not an expiry.
        const handler = unauthorizedHandler;
        unauthorizedHandler = null; // once: every request still in flight fails the same way
        handler();
    }
    if (!response.ok) {
        throw new ApiError(`Request to ${url} failed with status ${response.status}`, response.status, url, parsed);
    }
    return parsed;
}
